/**
 * 실행 표면(surface) 판별 — 같은 코드가 두 곳에서 돈다.
 *
 *  - `web`  : tarotwith.com (브라우저·PWA). 광고는 구글 애드센스
 *  - `toss` : 앱인토스 미니앱(웹뷰). 광고는 토스 인앱광고, 공유·외부링크도 토스 SDK
 *
 * 두 표면에 같은 화면을 띄우되 **광고·공유·외부링크 레이어만 갈아 끼운다**.
 * 애드센스와 토스 광고는 같은 화면에 함께 둘 수 없다 (doc/apps-in-toss.md §4).
 *
 * 빌드 시점 값이다 — `VITE_PLATFORM=toss pnpm --filter @tarot/web build`.
 * 런타임에 웹뷰를 냄새로 맞히지 않는 이유: 사용자 에이전트 추측은 틀리고,
 * 애드센스 스크립트는 index.html에 이미 박혀 나가므로 어차피 빌드에서 갈라야 한다.
 */
import { BRAND } from '@tarot/shared';

export const PLATFORMS = ['web', 'toss'] as const;
export type Platform = (typeof PLATFORMS)[number];

const DEFAULT_PLATFORM: Platform = 'web';

function readPlatform(): Platform {
  const raw = (import.meta.env.VITE_PLATFORM ?? '').trim();
  // 오타로 조용히 web이 되면 미니앱에 애드센스가 실려 나간다 — 눈에 띄게 경고하고 기본값으로
  if (raw && !(PLATFORMS as readonly string[]).includes(raw)) {
    console.warn(`알 수 없는 VITE_PLATFORM: "${raw}" — ${DEFAULT_PLATFORM}로 진행합니다`);
    return DEFAULT_PLATFORM;
  }
  return (raw || DEFAULT_PLATFORM) as Platform;
}

export const PLATFORM: Platform = readPlatform();

/** 토스 미니앱 안에서 돌고 있는가 — 광고·공유·외부링크 분기의 단일 기준 */
export const isToss = PLATFORM === 'toss';

/**
 * 웹(tarotwith.com)을 **토스 전용**으로 막는가 (2026-10-10~).
 * 켜지면 타로 보기 같은 서비스 화면은 모바일은 토스로 보내고 PC는 안내만 띄운다.
 * 카드 도감·용어 사전·안내·약관은 그대로 열린다 — components/TossOnlyGate.tsx.
 *
 * 기본값은 **운영 빌드 on · 개발 서버 off**다. 로컬 개발과 E2E 스크립트(scripts/e2e-*.mts)가
 * 웹 화면으로 흐름을 검증하기 때문이다. `VITE_WEB_GATE=on|off`로 어느 쪽이든 강제할 수 있고,
 * 되돌릴 때도 이 값을 off로 두고 다시 빌드하면 된다.
 */
export const webGateOn: boolean = (() => {
  if (isToss) return false;
  const raw = (import.meta.env.VITE_WEB_GATE ?? '').trim();
  if (raw === 'on') return true;
  if (raw === 'off') return false;
  return import.meta.env.PROD;
})();

/**
 * **밖으로 나가는 링크의 뿌리.**
 *
 * `location.origin`을 쓰면 안 된다 — 미니앱은 토스가 호스팅하는 도메인에서 돌기 때문에
 * 공유 링크가 `https://tarotwith.apps.tossmini.com/r/…` 로 나갔다. 카카오톡에서 그 링크를
 * 누르면 우리 서버가 아니라 토스 호스팅으로 가서 **흰 화면**이 뜨고, OG 태그도 없다
 * (2026-08-28 실기기 제보).
 *
 * 웹에서는 지금 주소를 그대로 쓴다 — 로컬 개발에서 운영 링크가 나가면 테스트가 꼬인다.
 */
export const publicOrigin = (): string => (isToss ? BRAND.siteUrl : location.origin);

/**
 * 외부 주소 열기 — **토스 미니앱에서만 호출한다**(웹은 평범한 새 탭이면 충분하다).
 *
 * 토스 웹뷰에서는 `target="_blank"`가 아무 일도 하지 않는다. SDK의 `openURL`로
 * 외부 브라우저를 띄우고, SDK가 아직 없으면 `window.open`으로 물러난다 —
 * 링크를 눌렀는데 아무 일도 일어나지 않는 것보다는 낫다.
 */
export async function openExternal(url: string): Promise<void> {
  const { loadTossSdk } = await import('./toss');
  const sdk = await loadTossSdk();
  if (sdk) {
    try {
      await sdk.openURL(url);
      return;
    } catch {
      // 아래 폴백
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
